import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { Send, Save, ArrowLeft, Plus, Trash2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const API = '/api';

const FIELD_TYPES = ['signature', 'initials', 'date', 'text', 'checkbox'];
const FIELD_COLORS = {
  signature: '#3b82f6',
  initials: '#8b5cf6',
  date: '#10b981',
  text: '#f59e0b',
  checkbox: '#ef4444',
};

let fieldCounter = 0;
function newFieldId() {
  return `field_${++fieldCounter}_${Date.now()}`;
}

export default function PDFEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [selectedFieldType, setSelectedFieldType] = useState('signature');
  const [selectedSignerIndex, setSelectedSignerIndex] = useState(0);
  const [signerCount, setSignerCount] = useState(1);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfToken] = useState(() => localStorage.getItem('token'));

  useEffect(() => {
    axios.get(`${API}/documents/${docId}`)
      .then(r => {
        setDoc(r.data);
        if (r.data.fields && r.data.fields.length > 0) {
          setFields(r.data.fields);
          const maxSigner = Math.max(...r.data.fields.map(f => f.signer_index), 0);
          setSignerCount(maxSigner + 1);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [docId]);

  const handlePageClick = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const fieldW = selectedFieldType === 'checkbox' ? 24 : 150;
    const fieldH = selectedFieldType === 'checkbox' ? 24 : 42;

    const field = {
      field_id: newFieldId(),
      type: selectedFieldType,
      page: currentPage,
      x: Math.max(0, x - fieldW / 2),
      y: Math.max(0, y - fieldH / 2),
      w: fieldW,
      h: fieldH,
      signer_index: selectedSignerIndex,
      required: true,
    };
    setFields(f => [...f, field]);
  }, [selectedFieldType, selectedSignerIndex, currentPage]);

  const handleFieldMouseDown = useCallback((e, fieldId) => {
    e.stopPropagation();
    setSelectedFieldId(fieldId);
    const startX = e.clientX;
    const startY = e.clientY;
    const field = fields.find(f => f.field_id === fieldId);
    if (!field) return;
    const origX = field.x;
    const origY = field.y;

    const onMove = (me) => {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;
      setFields(fs => fs.map(f => f.field_id === fieldId ? { ...f, x: origX + dx, y: origY + dy } : f));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [fields]);

  const deleteField = (fieldId) => {
    setFields(f => f.filter(x => x.field_id !== fieldId));
    setSelectedFieldId(null);
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/documents/${docId}/fields`, fields);
    } catch {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAndSend = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/documents/${docId}/fields`, fields);
      navigate(`/esign/send/${docId}`);
    } catch {
      alert('Save failed');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentPageFields = fields.filter(f => f.page === currentPage);

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3 flex-wrap z-10">
        <Link to="/esign" className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <span className="text-gray-300 hidden sm:block">|</span>
        <span className="font-medium text-gray-700 truncate max-w-xs text-sm hidden sm:block">{doc?.filename}</span>
        <span className="text-gray-300 hidden sm:block">|</span>

        {/* Field type selector */}
        <div className="flex items-center gap-1 flex-wrap">
          {FIELD_TYPES.map(t => (
            <button
              key={t}
              onClick={() => setSelectedFieldType(t)}
              className={`px-2 py-1 rounded text-xs font-medium capitalize transition-colors ${
                selectedFieldType === t ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={selectedFieldType === t ? { backgroundColor: FIELD_COLORS[t] } : {}}
            >
              {t}
            </button>
          ))}
        </div>

        <span className="text-gray-300">|</span>

        {/* Signer selector */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Signer:</span>
          {Array.from({ length: signerCount }, (_, i) => (
            <button
              key={i}
              onClick={() => setSelectedSignerIndex(i)}
              className={`w-7 h-7 rounded-full text-xs font-bold transition-colors ${
                selectedSignerIndex === i ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setSignerCount(c => Math.min(c + 1, 10))}
            className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        <div className="flex-1" />

        <button
          onClick={saveFields}
          disabled={saving}
          className="flex items-center gap-1 bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-gray-700 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save
        </button>
        <button
          onClick={saveAndSend}
          disabled={saving}
          className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 disabled:opacity-60"
        >
          <Send className="w-3 h-3" /> Send
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* PDF viewer + field overlay */}
        <div className="flex-1 overflow-auto flex flex-col items-center py-6 px-4">
          <p className="text-xs text-gray-400 mb-3">
            Click on the PDF to place a <strong className="capitalize" style={{ color: FIELD_COLORS[selectedFieldType] }}>{selectedFieldType}</strong> field for Signer #{selectedSignerIndex + 1}. Drag to reposition.
          </p>

          <div
            ref={containerRef}
            className="relative bg-white shadow-lg cursor-crosshair select-none"
            onClick={handlePageClick}
          >
            <Document
              file={{ url: `${API}/documents/${docId}/download`, httpHeaders: { Authorization: `Bearer ${pdfToken}` } }}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="w-[700px] h-[900px] flex items-center justify-center text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading PDF...
                </div>
              }
              error={<div className="w-[700px] h-[400px] flex items-center justify-center text-red-400">Failed to load PDF</div>}
            >
              <Page pageNumber={currentPage} width={700} />
            </Document>

            {/* Field overlays */}
            {currentPageFields.map(field => (
              <div
                key={field.field_id}
                onMouseDown={(e) => handleFieldMouseDown(e, field.field_id)}
                onClick={e => { e.stopPropagation(); setSelectedFieldId(field.field_id); }}
                style={{
                  position: 'absolute',
                  left: field.x,
                  top: field.y,
                  width: field.w,
                  height: field.h,
                  border: `2px solid ${FIELD_COLORS[field.type]}`,
                  backgroundColor: `${FIELD_COLORS[field.type]}22`,
                  cursor: 'move',
                  boxSizing: 'border-box',
                  zIndex: 10,
                  outline: selectedFieldId === field.field_id ? `2px solid ${FIELD_COLORS[field.type]}` : 'none',
                  outlineOffset: 2,
                }}
              >
                <div style={{
                  fontSize: 9,
                  color: FIELD_COLORS[field.type],
                  padding: '1px 3px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}>
                  {field.type} (#{field.signer_index + 1})
                </div>
                {selectedFieldId === field.field_id && (
                  <button
                    onMouseDown={(e) => { e.stopPropagation(); deleteField(field.field_id); }}
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: 12,
                      lineHeight: '18px',
                      textAlign: 'center',
                      zIndex: 20,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Page nav */}
          {numPages > 1 && (
            <div className="flex items-center gap-4 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">Page {currentPage} of {numPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                disabled={currentPage === numPages}
                className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-60 bg-white border-l border-gray-200 overflow-y-auto p-4 flex-shrink-0">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">
            Fields ({fields.length})
          </h3>
          {fields.length === 0 ? (
            <p className="text-xs text-gray-400">Click on the PDF to add fields</p>
          ) : (
            <div className="space-y-1.5">
              {fields.map(f => (
                <div
                  key={f.field_id}
                  className={`flex items-center justify-between p-2 rounded-lg text-xs border cursor-pointer transition-colors ${
                    selectedFieldId === f.field_id
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedFieldId(f.field_id)}
                >
                  <div className="min-w-0">
                    <span className="font-medium capitalize" style={{ color: FIELD_COLORS[f.type] }}>
                      {f.type}
                    </span>
                    <span className="text-gray-400 ml-1">Pg {f.page} · #{f.signer_index + 1}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteField(f.field_id); }}
                    className="text-red-400 hover:text-red-600 ml-1 flex-shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
